<template>
  <div class="group group--wide group--sidebar group--schedule">
    <div class="group__heading">
      <h1 
        :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
        class="h1 group__title mw600 mb0 m0">Upcoming availability</h1>
      <div class="query-button">
        <p>Need an alternative date?</p>
        <p 
          @click="submitAppointmentQuery()" 
          class="cp fw-bold c-accent"
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
        >
          
          Submit an appointment request
        </p>
      </div>
    </div>

    <div class="schedule">
      <button
        @click="loadWeek(`prev`)"
        :class="[
          `schedule__prev`,
          (prevStepDisabled || forceDisabledPrev) ? `schedule__prev--disabled` : ``
        ]"
        ontouchstart=""
        :disabled="prevStepDisabled || forceDisabledPrev"
      />

      <ul
        ref="scheduleWeek"
        :class="[
          `schedule__week`,
          allSlotsDisabled ? `schedule__week--no-scroll` : ``
        ]"
      >
        <transition name="schedule" mode="out-in">
          <div
            v-if="loadingAvailabilities || allSlotsDisabled"
            class="schedule__loader"
          >
            <module-dots v-if="loadingAvailabilities" :active="true" />

            <p
              v-if="allSlotsDisabled && !loadingAvailabilities && !noAvailableSlots"
              class="h2 tac m0">

              There are no sessions available this week. <br> Use <span class="icon icon--next" /> to view future dates.
            </p>

            <div v-if="!loadingAvailabilities && noAvailableSlots" class="tac">
              <h2 class="mb-">No available timeslots</h2>
              <p class="p mt0">Try requesting an appointment with this counselor.</p>
              <module-button
                class="mt-"
                button="primary"
                @click.native="submitAppointmentQuery()"
              >
                Submit an appointment request
              </module-button>
            </div>
          </div>
        </transition>

        <li
          v-for="(day, index) in availabilities"
          :key="index"
          :class="[
            `schedule__day`,
            (index === availabilities.length - 1 ||
              index === availabilities.length - 2) &&
              !loadingAvailabilities
              ? `schedule__day--weekend schedule__day--weekend-${index}`
              : ``,
            index === availabilities.length - 1 ? `schedule__day--last` : ``
            
          ]"
        >
          <ul>
            <li
              
              class="schedule__heading"
              v-html="formatTitle(availabilityTitles[index])"
            />

            <li 
              v-for="(slot, index) in getSlots(day)"
              :key="index"
              :class="[
                `schedule__time`,
                `fw-medium`,
                allSlotsDisabled || loadingAvailabilities
                  ? `schedule__time--hidden`
                  : ``,
                slot.disabled ? `schedule__time--disabled` : ``
              ]"
              :ref="slot"
              @click="selectTimeSlot(slot)"
            >
              <span 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="schedule__label">{{ slot.timeslot | time }}</span>
            </li>
          </ul>
        </li>

        <button
          ontouchstart=""
          :class="[`schedule__next`, `hide-above-medium`]"
          @click="loadWeek(`next`)"
          :disabled="noAvailableSlots"
        />
      </ul>

      <button
        ontouchstart=""
        :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
        :class="[
          `schedule__next`,
          allSlotsDisabled && !loadingAvailabilities ? `` : `hide-below-medium`,
          (noAvailableSlots) ? `schedule__next--disabled` : ``
        ]"
        @click="loadWeek(`next`)"
        :disabled="noAvailableSlots"
      />
    </div>

    <div
      v-if="!loadingAvailabilities && getLongestArrayLength() > 5"
      @click="extendScheduler"
      :class="[
        `schedule schedule__expand`,
        slotsToShow > 5 ? `schedule__expand--expanded` : ``
      ]"
    >
      <span 
        v-if="!loadingAvailabilities" 
        class="expand__label"
      >{{ slotsToShow > 5 ? `Less` : `More` }} times</span
      >
    </div>

    <div
      v-if="loadingAvailabilities || getLongestArrayLength() <= 5"
      class="schedule schedule__spacer"
    />

    <module-disclaimer class="disclaimer--schedule" />

    <transition name="intake" mode="out-in">
      <div
        v-if="loadingProviderDetails"
        class="bbg bbg--white group group--wide provider-detail__loader"
      >
        <module-dots :active="true" />
      </div>

      <article
        v-if="!loadingProviderDetails"
        class="bbg bbg--white group group--wide"
      >
        <section v-if="overview" class="section section--approach ovh">
          <h2 
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="h2">Overview</h2>

          <p class="p">{{ overview }}</p>
        </section>

        <section v-if="specialties" class="section section--approach ovh">
          <h2 
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="h2">Specialities</h2>

          <p class="p">{{ specialties }}</p>
        </section>

        <section class="section section--things-to-know">
          <div class="section__group">
            <div v-if="languages" class="section__group-item">
              <h2 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="h2">Languages</h2>
              <p class="p">{{ languages }}</p>
            </div>

            <div v-if="experience" class="section__group-item">
              <h2 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="h2">Experience</h2>
              <p class="p">{{ experience }} years</p>
            </div>

            <div v-if="location" class="section__group-item">
              <h2 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="h2">Location</h2>
              <p class="p">{{ location }}</p>
            </div>
          </div>
        </section>

        <section v-if="education" class="section section--approach ovh">
          <h2 class="h2">Education</h2>

          <p class="p">{{ education }}</p>
        </section>

        <section
          v-if="licensedStates"
          class="section section--approach ovh pab2"
        >
          <h2 
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="h2">Licensed States</h2>

          <p class="p">{{ licensedStates }}</p>
        </section>
      </article>
    </transition>
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex';

// 3rd party
import moment from 'moment';

// Modules
import ModuleDisclaimer from 'components/Disclaimer';
import ModuleDots from 'components/Dots';

// Mixins
import MixinIntakeProgress from 'components/IntakeProgress';

// Export
export default {
  name: 'view-schedule',

  // Components
  components: {
    ModuleDisclaimer,
    ModuleDots,
  },

  // Filters
  filters: {
    time(time) {
      if (!time) return;

      return moment(time).format('h:mma');
    },
  },

  // Mixins
  mixins: [MixinIntakeProgress],

  // Data
  data() {
    return {
      dateToRequest: moment().format(),
      slotsToShow: 5,
      showApptRequest: false,
      noAvailableSlots: false,
      nextAvailableSlot: null,
      forceDisabledPrev: false,
    };
  },

  // Computed
  computed: {
    ...mapGetters('provider', [
      'loadingAvailabilities',
      'loadingProviderDetails',
      'provider',
      'providerDetails',
      'availabilityTitles',
      'availabilities',
    ]),
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('app', ['org']),

    prevStepDisabled() {
      return moment(this.dateToRequest).isBefore(moment()) || moment(this.nextAvailableSlot).isBefore(moment());
    },

    pd() {
      if (this.providerDetails) return this.providerDetails;
    },

    overview() {
      if (this.pd && this.pd.about_me) {
        return this.pd.about_me;
      }
    },

    specialties() {
      if (this.pd && this.pd.specialties) {
        return this.getCommaSeparatedString(this.pd.specialties, 'name');
      }
    },

    education() {
      if (this.pd && this.pd.education) {
        return this.getCommaSeparatedString(this.pd.education, 'name');
      }
    },

    languages() {
      if (this.pd && this.pd.languages) {
        return this.getCommaSeparatedString(this.pd.languages, 'name');
      }
    },

    experience() {
      if (this.pd && this.pd.years_in_practice) {
        return this.pd.years_in_practice;
      }
    },

    location() {
      if (this.pd && this.pd.city) {
        return `${this.pd.city}, ${this.pd.state_abbrev}`;
      }
    },

    licensedStates() {
      if (this.pd && this.pd.licenses) {
        return this.getCommaSeparatedString(this.pd.licenses, 'state');
      }
    },

    allSlotsDisabled() {
      const slots = _.every(_.flattenDeep(this.availabilities), ['disabled', true]);
      if (slots && !this.forceDisabledPrev && !this.noAvailableSlots) {
        this.loadWeek('initial');
      }
      return slots;
    },

    noDatesAvailable() {
      return _.every(this.availabilities, day => _.isEmpty(day)); // eslint-disable-line no-undef
    },
  },

  // Methods
  methods: {
    ...mapActions('provider', [
      'getProviderDetail',
      'getProviderAvailability',
      'selectSlot',
      'getNextAvailability',
    ]),

    formatTitle(date) {
      if (!date) return;

      let title;

      if (moment(date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
        title = moment(date).format('[Today][\n] MMM. D');
      } else if (
        moment(date).format('YYYY-MM-DD') ===
        moment()
          .add(1, 'd')
          .format('YYYY-MM-DD')
      ) {
        title = moment(date).format('[Tomorrow][\n] MMM. D');
      } else {
        title = moment(date).format('dddd[\n] MMM. D');
      }

      return `${title.split('\n')[0]}<br>${title.split('\n')[1]}`;
    },

    getLongestArrayLength() {
      const slotCount = [];

      // Count the amount of slots for each day and store them in an array
      _.forEach(this.availabilities, i => {
        // eslint-disable-line no-undef
        slotCount.push(i.length);
      });

      // Get the length of the day with the most available slots
      return this.availabilities[slotCount.indexOf(_.max(slotCount))].length; // eslint-disable-line no-undef
    },

    getSlots(day) {
      if (this.noDatesAvailable) {
        if (!this.loadingAvailabilities) {
          this.showApptRequest = true;
        }

        for (let i = 0; i < 5; i++) {
          day.push({ disabled: true });
        }

        return day;
      } else if (day.length === 0) {
        const max =
          this.getLongestArrayLength() < 5 ? 5 : this.getLongestArrayLength();

        for (let i = 0; i < max; i++) {
          day.push({ disabled: true });
        }
      }

      return day.slice(0, this.slotsToShow);
    },

    extendScheduler() {
      this.slotsToShow =
        this.slotsToShow === 5 ? this.getLongestArrayLength() : 5;
    },

    loadWeek(indicator) {
      if (this.$refs.scheduleWeek) this.$refs.scheduleWeek.scrollTo(0, 0);

      if (
        (indicator === 'prev' && this.prevStepDisabled) ||
        this.loadingAvailabilities
      )
        return;

      this.slotsToShow = 5;

      if (indicator === 'initial' && this.nextAvailableSlot !== null) {
        this.forceDisabledPrev = true;
        this.dateToRequest = this.nextAvailableSlot;
      } else if (indicator === 'next') {
        this.dateToRequest = moment(this.dateToRequest).add(7, 'd').format();
        this.forceDisabledPrev = false;
      } else if (indicator === 'prev') {
        this.dateToRequest = moment(this.dateToRequest).subtract(7, 'd').format();
      }

      this.getProviderAvailability(this.dateToRequest);
    },

    getCommaSeparatedString(arr, key) {
      const items = [];

      _.forEach(arr, item => {
        // eslint-disable-line no-undef
        items.push(item[key]);
      });

      return items.join(', ');
    },

    selectTimeSlot(slot) {
      if (slot.disabled) return;

      this.selectSlot(slot);

      this.$router.push({
        name: 'appointment',
      });
    },

    submitAppointmentQuery() {
      this.$router.push({
        name: 'provider-query',
      });
    },
  },

  created() {
    if (!this.isAuthenticated) {
      if (
        !this.provider &&
        this.checkIntakeRequirements('default', 'intakeDetail', 2) &&
        this.checkIntakeRequirements('default', null, null)
      ) {
        this.$router.push({ name: 'results' });
      }

      if (
        !this.checkIntakeRequirements('default', 'intakeDetail', 2) &&
        !this.checkIntakeRequirements('default', null, null)
      ) {
        this.$router.push({ name: 'home' });

        return;
      }

      this.checkIntakeRequirements('default', 'intakeDetail', 2, 'help');
    }

    if (!this.provider) {
      this.$router.push({ name: 'results' });
      return;
    }

    this.getNextAvailability()
      .then((response) => {
        const slots = response.data.availability_details && response.data.availability_details;
        if (slots.available_hours && slots.available_hours.length > 0) {
          this.nextAvailableSlot = slots.available_hours[0].timeslot;
        } else {
          this.noAvailableSlots = true;
        }
      });

    this.getProviderDetail(this.dateToRequest);
  },

  destroyed() {
    this.noAvailableSlots = null;
  },
};
</script>



// WEBPACK FOOTER //
// Schedule.vue?1537c708